import { Canvas } from 'fabric/fabric-impl';
import { useCallback, useState } from 'react';

import { DEFAULT_ZOOM } from '~/core/constants';
import { drawingTool } from '~/core/engine/DrawingTool';
import {
  TOOLBAR,
  TOOLBAR_ENABLING,
  UserDesignOperation,
  mainButtons,
  zoomButtons,
} from '~/core/enums';
import {
  autoZoomPanToFit,
  updateShapeByRotation,
  zoomIn,
  zoomOut,
} from '~/core/helpers';
import { useActiveAppliance } from '~/core/hooks/useActiveAppliance';
import useAppliance from '~/core/hooks/useAppliance';
import { useRegisterUserAction } from '~/core/hooks/useRegisterUserAction';
import { useToolbarHelper } from '~/core/hooks/useToolbarHelper';
import { usePricedProductsContext } from '~/legacy/easy-estimator-page/context';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import {
  setPreventRepricing,
  updateNeedRedrawCanvas,
} from '~/redux/slices/drawingShape';
import { selectFabricCanvas } from '~/redux/slices/fabric';
import { updateManualZoom } from '~/redux/slices/toolbarCanvas';
import { useDrawingShapeContext, useUndoStackContext } from '~contexts';

type Button = {
  name: TOOLBAR;
  isEnabled: boolean;
  onClick: () => void;
};

const useToolBar = () => {
  const dispatch = useAppDispatch();
  const {
    getters: {
      drawingShapes,
      activeDrawingShape,
      activeDrawingApplianceId,
      areThereShapesInTheDrawingArea,
    },
    handlers: { updateDrawingShapes },
  } = useDrawingShapeContext();

  const { pullFromUndoStack, shapesStack, undoIndex, maxUndoIndex } =
    useUndoStackContext();

  const canvas = useAppSelector(selectFabricCanvas);

  const { removeApplianceFromDrawingArea } = useAppliance();
  const { rotateActiveDrawingAppliance } = useActiveAppliance();
  const { registerUserAction } = useRegisterUserAction();
  const { resetPricedProducts } = usePricedProductsContext();

  const { duplicateShape, removeActiveShape, cleanupCutoutsOnActiveShape } =
    useToolbarHelper();

  const [isModalVisible, setIsModalVisible] = useState(false);
  const isBtnEnabled = !!activeDrawingShape || !!activeDrawingApplianceId;
  const btnFlag: Record<TOOLBAR_ENABLING, boolean> = {
    [TOOLBAR.REDO]: (undoIndex ?? Infinity) < maxUndoIndex,
    [TOOLBAR.UNDO]: shapesStack.length > 0 && areThereShapesInTheDrawingArea,
    [TOOLBAR.REMOVE]: !isModalVisible,
    [TOOLBAR.DUPLICATE]: !!activeDrawingShape,
  };
  const btnAction: Record<TOOLBAR, () => void> = {
    [TOOLBAR.REDO]: () => {
      redoOneAction();
    },
    [TOOLBAR.UNDO]: () => {
      undoOneAction();
    },
    [TOOLBAR.ROTATE_CLOCKWISE]: () => {
      rotateDrawingShapeOrAppliance(true, canvas);
      registerUserAction(UserDesignOperation.ROTATE_CLOCKWISE);
    },
    [TOOLBAR.ROTATE_COUNTERCLOCKWISE]: () => {
      rotateDrawingShapeOrAppliance(false, canvas);
      registerUserAction(UserDesignOperation.ROTATE_COUNTERCLOCKWISE);
    },
    [TOOLBAR.REMOVE]: () => {
      openModal();
    },
    [TOOLBAR.DUPLICATE]: () => {
      duplicateActiveDrawingShape();
    },
    [TOOLBAR.ZOOM_IN]: () => {
      zoomIn(canvas);
      dispatch(updateManualZoom(true));
    },
    [TOOLBAR.ZOOM_OUT]: () => {
      zoomOut(canvas);
      dispatch(updateManualZoom(true));
    },
    [TOOLBAR.ZOOM_TO_FIT]: () => {
      autoZoomPanToFit(canvas, true, true);
      dispatch(updateManualZoom(false));
    },
  };
  const toolbarButtons = [mainButtons, zoomButtons].map((btns) =>
    btns.map((btn) => ({
      name: btn,
      isEnabled: btnFlag[btn as TOOLBAR_ENABLING] ?? isBtnEnabled,
      onClick: () => {
        btnAction[btn]();
        canvas.renderAll();
      },
    }))
  ) as [Button[], Button[]];

  const closeModal = () => setIsModalVisible(false);
  const openModal = () => setIsModalVisible(true);

  const duplicateActiveDrawingShape = useCallback(() => {
    if (!areThereShapesInTheDrawingArea || !activeDrawingShape) {
      return;
    }

    const shapeIndex = drawingShapes.findIndex(
      ({ id }) => id === activeDrawingShape?.id
    );

    if (shapeIndex > -1) {
      duplicateShape(shapeIndex);
      registerUserAction(UserDesignOperation.DUPLICATE_SHAPE);
    }
  }, [
    areThereShapesInTheDrawingArea,
    activeDrawingShape,
    drawingShapes,
    duplicateShape,
    registerUserAction,
  ]);

  const rotateActiveDrawingShape = useCallback(
    (clockwise: boolean) => {
      if (!areThereShapesInTheDrawingArea || !activeDrawingShape) {
        return;
      }

      // rotate selected shape
      dispatch(setPreventRepricing(true));
      updateShapeByRotation(canvas, activeDrawingShape, clockwise);
      updateDrawingShapes([...drawingShapes]);
      dispatch(updateNeedRedrawCanvas(true));
    },
    [
      activeDrawingShape,
      canvas,
      dispatch,
      drawingShapes,
      areThereShapesInTheDrawingArea,
      updateDrawingShapes,
    ]
  );

  const undoOneAction = useCallback(() => {
    registerUserAction(UserDesignOperation.UNDO);

    if ((undoIndex ?? 0) > 0) {
      pullFromUndoStack('undo');
    } else {
      removeActiveShape();
    }
  }, [registerUserAction, undoIndex, pullFromUndoStack, removeActiveShape]);

  const redoOneAction = useCallback(() => {
    registerUserAction(UserDesignOperation.REDO);
    pullFromUndoStack('redo');
  }, [pullFromUndoStack, registerUserAction]);

  const rotateDrawingShapeOrAppliance = useCallback(
    (clockwise: boolean, canvas: Canvas) => {
      if (activeDrawingShape) {
        rotateActiveDrawingShape(clockwise);
      } else {
        rotateActiveDrawingAppliance(canvas, clockwise);
      }
    },
    [activeDrawingShape, rotateActiveDrawingAppliance, rotateActiveDrawingShape]
  );

  const removeShapeOrAppliance = useCallback(() => {
    if (!areThereShapesInTheDrawingArea || !canvas) {
      return;
    }

    drawingTool.clearCanvas({ canvas });

    const updatedDrawingShapes = drawingShapes.filter(
      (shape) => shape.id !== activeDrawingShape?.id
    );

    if (
      activeDrawingShape &&
      updatedDrawingShapes.length !== drawingShapes.length
    ) {
      // Find the shape being deleted from the current drawingShapes array
      const shapeBeingDeleted = drawingShapes.find(
        (shape) => shape.id === activeDrawingShape.id
      );

      // cleanup cutouts before removing the shape
      // Use the shape from the current array to ensure we're cleaning up
      // the correct shape's appliances (appliances that were moved away
      // are no longer in this shape's appliances array)
      if (shapeBeingDeleted) {
        cleanupCutoutsOnActiveShape(shapeBeingDeleted);
      }

      removeActiveShape(updatedDrawingShapes);
      registerUserAction(UserDesignOperation.REMOVE_SHAPE);

      // If the canvas is now empty (no shapes), reset zoom to default.
      if (updatedDrawingShapes.length <= 1) {
        canvas.setZoom(DEFAULT_ZOOM);
        autoZoomPanToFit(canvas, true, true);
      }
    } else if (activeDrawingApplianceId) {
      removeApplianceFromDrawingArea();
      registerUserAction(UserDesignOperation.REMOVE_APPLIANCE);
    }

    resetPricedProducts();
  }, [
    activeDrawingApplianceId,
    areThereShapesInTheDrawingArea,
    canvas,
    cleanupCutoutsOnActiveShape,
    registerUserAction,
    removeApplianceFromDrawingArea,
    removeActiveShape,
    resetPricedProducts,
    activeDrawingShape,
    drawingShapes,
  ]);

  return {
    toolbarButtons,
    isModalVisible,
    closeModal,
    removeShapeOrAppliance,
  };
};

export default useToolBar;
